import { VStack, Heading, Text, Box, HStack, Badge, Grid, GridItem, Progress, Button } from '@chakra-ui/react'
import { CreditCard, AlertCircle, ExternalLink } from 'lucide-react'
import { useSubscription } from '@/shared/hooks/useSubscription'

export function SubscriptionPage() {
  const subscription = useSubscription()

  const tierName = subscription.tierName || 'Essentiel'

  // Calcul des jauges d'utilisation
  const resources = [
    {
      key: 'residences' as const,
      label: 'Résidences',
      current: subscription.usage?.residences || 0,
      max: subscription.limits?.residences || 1,
    },
    {
      key: 'units' as const,
      label: 'Lots',
      current: subscription.usage?.units || 0,
      max: subscription.limits?.units || 25,
    },
    {
      key: 'users' as const,
      label: 'Utilisateurs',
      current: subscription.usage?.users || 1,
      max: subscription.limits?.users || 1,
    },
  ]

  return (
    <VStack align="stretch" gap={6}>
      {/* Header */}
      <VStack align="start" gap={2}>
        <HStack gap={2}>
          <CreditCard size={24} />
          <Heading as="h2" fontSize="2xl">
            Abonnement
          </Heading>
        </HStack>
        <Text color="gray.600">
          Gérez votre formule et suivez votre utilisation
        </Text>
      </VStack>

      {/* Current Plan */}
      <Box
        p={6}
        bg="brand.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="brand.200"
      >
        <VStack align="start" gap={3}>
          <HStack justify="space-between" w="full">
            <VStack align="start" gap={1}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Formule actuelle
              </Text>
              <Heading as="h3" fontSize="2xl">
                {tierName}
              </Heading>
            </VStack>
            <Badge colorPalette={subscription.isActive ? 'green' : 'gray'} size="lg">
              {subscription.isActive ? 'Actif' : 'Inactif'}
            </Badge>
          </HStack>
        </VStack>
      </Box>

      {/* Usage Stats */}
      <VStack align="stretch" gap={4}>
        <Heading as="h3" fontSize="lg">
          Utilisation
        </Heading>

        <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
          {resources.map((resource) => {
            const percentage = subscription.getUsagePercentage(resource.key)
            const isUnlimited = resource.max === -1
            const isAtLimit = subscription.isAtLimit(resource.key)
            const isNearLimit = subscription.isNearLimit(resource.key) && !isAtLimit

            return (
              <GridItem key={resource.key}>
                <Box
                  p={4}
                  bg="white"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <VStack align="stretch" gap={3}>
                    <HStack justify="space-between">
                      <Text fontWeight="medium" fontSize="sm" color="gray.700">
                        {resource.label}
                      </Text>
                      {isAtLimit && (
                        <Badge colorPalette="red" size="sm">
                          <HStack gap={1}>
                            <AlertCircle size={12} />
                            <span>Limite atteinte</span>
                          </HStack>
                        </Badge>
                      )}
                      {isNearLimit && (
                        <Badge colorPalette="orange" size="sm">
                          <HStack gap={1}>
                            <AlertCircle size={12} />
                            <span>Limite proche</span>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontSize="2xl" fontWeight="bold">
                        {resource.current}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {isUnlimited ? 'Illimité' : `/ ${resource.max}`}
                      </Text>
                    </HStack>

                    {!isUnlimited && (
                      <Progress.Root
                        value={percentage}
                        colorPalette={isAtLimit ? 'red' : isNearLimit ? 'orange' : 'brand'}
                        size="sm"
                      >
                        <Progress.Track borderRadius="full">
                          <Progress.Range />
                        </Progress.Track>
                      </Progress.Root>
                    )}
                  </VStack>
                </Box>
              </GridItem>
            )
          })}
        </Grid>
      </VStack>

      {/* Manage Subscription */}
      <VStack align="stretch" gap={4}>
        <Heading as="h3" fontSize="lg">
          Gestion de l'abonnement
        </Heading>

        <Box
          p={6}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack align="stretch" gap={4}>
            <Text color="gray.700">
              Accédez à votre espace client Stripe pour modifier votre abonnement, mettre à jour
              vos informations de paiement ou consulter votre historique de facturation.
            </Text>
            <HStack>
              <a
                href="https://billing.stripe.com/p/login/test_5kQ14pb8Bh1Ob5X7N55os00"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <Button
                  colorPalette="brand"
                  size="lg"
                >
                  <CreditCard size={20} />
                  Gérer mon abonnement
                  <ExternalLink size={16} />
                </Button>
              </a>
            </HStack>
          </VStack>
        </Box>
      </VStack>

      {/* Coming Soon: Upgrade Options, Payment History, Invoices */}
      <Box
        p={6}
        bg="gray.50"
        borderRadius="lg"
        border="1px dashed"
        borderColor="gray.300"
        textAlign="center"
      >
        <Text color="gray.600" fontSize="sm">
          Historique de facturation et options de mise à niveau à venir
        </Text>
      </Box>
    </VStack>
  )
}
